"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function Privacy() {
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: metaRef,
    isVisible: metaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: sec1Ref,
    isVisible: sec1Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const {
    ref: sec2Ref,
    isVisible: sec2Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const {
    ref: sec3Ref,
    isVisible: sec3Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const {
    ref: sec4Ref,
    isVisible: sec4Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const {
    ref: sec5Ref,
    isVisible: sec5Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const {
    ref: sec6Ref,
    isVisible: sec6Visible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="4124" id="privacy-policy" className="relative bg-background text-foreground min-h-screen py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-20 overflow-hidden">
      {/* Noise texture overlay */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")"
    }} />

      <div className="relative mx-auto max-w-[1400px]">
        {/* Eyebrow Label */}
        <div ref={labelRef} className={`flex justify-start mb-6 transition-all duration-700 ease-out ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}>
          <span className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase" style={{
          color: "var(--primary)"
        }}>
            PRIVACY POLICY
          </span>
        </div>

        {/* Hero Headline */}
        <h1 ref={headlineRef} className={`font-default font-semibold text-foreground tracking-tight text-left mb-6 transition-all duration-1000 ease-out text-4xl sm:text-5xl md:text-6xl lg:text-7xl ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          Privacy Policy
        </h1>

        {/* Document Meta */}
        <div ref={metaRef} className={`flex flex-wrap items-center gap-6 text-sm text-muted-foreground mb-12 pb-8 border-b border-border transition-all duration-700 ease-out ${metaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span>Last updated: 27.09.2026</span>
          <span className="w-1 h-1 rounded-full bg-border" aria-hidden="true" />
          <span>Effective date: 27.09.</span>
          <span className="w-1 h-1 rounded-full bg-border" aria-hidden="true" />
          <span>Resseti Educational Technologies</span>
        </div>

        {/* Main Legal Content - Single readable column */}
        <div className="max-w-4xl space-y-16 text-foreground/90 font-default text-base leading-relaxed">
          {/* Section 1 */}
          <div ref={sec1Ref} className={`space-y-6 transition-all duration-700 ease-out ${sec1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground flex items-baseline gap-3">
              <span className="font-mono text-lg" style={{
              color: "var(--primary)"
            }}>
                01 //
              </span>
              1. Who we are
            </h2>

            <p>This Privacy Policy applies to the services and platforms operated by Resseti Ltd ("Resseti", "we", "us", or "our"), registered in England and Wales with registered office at 3 Bannold Court, Waterbeach, Cambridge. Our data protection registration number with the UK Information Commissioner's Office (ICO) is [ZA ].</p>

            <p>
              If you have any questions about this Privacy Policy, our data practices, or how your personal data is handled, you can contact our Data Protection lead at{" "}
              <a href="mailto:[privacy@resseti.com]" className="underline hover:opacity-80 transition-opacity" style={{
              color: "var(--primary)"
            }}>privacy@resseti.com</a>
              .
            </p>

            <p>
              Depending on how you interact with our platform and diagnostic tools (such as Lumina), we operate under two distinct roles under UK and EU data protection law:
            </p>

            {/* Controller / Processor Table */}
            <div className="overflow-x-auto my-6 border border-border rounded-md">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border bg-card/60">
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Context
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Our Role
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Data Subjects
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">School contracts (Lumina pilot / institutional license)</td>
                    <td className="py-3 px-4 align-top font-mono text-xs">Data Processor</td>
                    <td className="py-3 px-4 align-top">Students, teachers, school administrators</td>
                    <td className="py-3 px-4 align-top">We process student work, marks, and teacher feedback strictly on the written instructions of the school under a Data Processing Agreement (DPA). The school remains the Data Controller.</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Website visitors, school enquiry contacts, individual pilot signups</td>
                    <td className="py-3 px-4 align-top font-mono text-xs">Data Controller</td>
                    <td className="py-3 px-4 align-top">Website visitors, prospective partner staff, enquiry submitters</td>
                    <td className="py-3 px-4 align-top">We determine the purposes and means of processing contact details, technical logs, communication records, and prospective pilot inquiries.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Separator className="bg-border/60" />

          {/* Section 2 */}
          <div ref={sec2Ref} className={`space-y-6 transition-all duration-700 ease-out ${sec2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground flex items-baseline gap-3">
              <span className="font-mono text-lg" style={{
              color: "var(--primary)"
            }}>
                02 //
              </span>
              2. Data we collect
            </h2>

            <p>
              We only collect personal data that is necessary to fulfill our institutional service commitments, maintain platform security, and respond to educator enquiries.
            </p>

            {/* Data Categories Table */}
            <div className="overflow-x-auto my-6 border border-border rounded-md">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border bg-card/60">
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Category
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Types of Data
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">School / Teacher Contact Data</td>
                    <td className="py-3 px-4 align-top">Name, school email address, school name, job title / role, department, enquiry message details</td>
                    <td className="py-3 px-4 align-top">Provided directly by you via forms, emails, or pilot agreements</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Student Assessment &amp; Work Data</td>
                    <td className="py-3 px-4 align-top">Student pseudonymised identifier / student ID, assessment responses, typed answers, step-by-step working, marks awarded, diagnostic failure-point classifications [Delete or amend if handwriting upload is added.]</td>
                    <td className="py-3 px-4 align-top">Provided by the school or entered by the student through the Lumina interface under the school&apos;s account</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Technical &amp; Usage Data</td>
                    <td className="py-3 px-4 align-top">IP address, browser type, device information, operating system, page views, session timestamps, diagnostic error logs [List any analytics tool used on 10Web.]</td>
                    <td className="py-3 px-4 align-top">Collected automatically via server logs and essential cookies when you access the platform</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Communications Data</td>
                    <td className="py-3 px-4 align-top">Correspondence between you and Resseti regarding support, pilot feedback, or product enquiries</td>
                    <td className="py-3 px-4 align-top">Direct communications</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm text-muted-foreground">
              We do not intentionally collect any special category personal data (such as health data, biometric data, or racial/ethnic origin) or criminal convictions data.
            </p>
          </div>

          <Separator className="bg-border/60" />

          {/* Section 3 */}
          <div ref={sec3Ref} className={`space-y-6 transition-all duration-700 ease-out ${sec3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground flex items-baseline gap-3">
              <span className="font-mono text-lg" style={{
              color: "var(--primary)"
            }}>
                03 //
              </span>
              3. How we use data and our lawful bases
            </h2>

            <p>
              Under the UK GDPR and EU GDPR, we must have a valid lawful basis to process your personal data. Where we act as a Data Controller, the table below outlines our purposes and lawful bases. Where we act as a Data Processor on behalf of a school, our processing is governed by our contract with the school (Article 28 DPA).
            </p>

            {/* Purposes and Lawful Bases Table */}
            <div className="overflow-x-auto my-6 border border-border rounded-md">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border bg-card/60">
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Purpose
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Data Used
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Lawful Basis (GDPR / UK GDPR)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Providing the Lumina diagnostic engine to schools</td>
                    <td className="py-3 px-4 align-top">Student assessment data, teacher accounts</td>
                    <td className="py-3 px-4 align-top">Performance of contract (where school is controller, processed under DPA / Art. 28)</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Responding to pilot enquiries and school demonstrations</td>
                    <td className="py-3 px-4 align-top">School / teacher contact data, communications</td>
                    <td className="py-3 px-4 align-top">Legitimate interests (evaluating and establishing institutional partnerships) or Contract preparation</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">System security, error diagnosis, and infrastructure stability</td>
                    <td className="py-3 px-4 align-top">Technical &amp; usage logs</td>
                    <td className="py-3 px-4 align-top">Legitimate interests (maintaining platform resilience and security) / Legal obligation</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Diagnostic model evaluation and rule-layer refinement (using aggregated, de-identified data only)</td>
                    <td className="py-3 px-4 align-top">De-identified assessment step patterns</td>
                    <td className="py-3 px-4 align-top">Legitimate interests (ensuring diagnostic precision; individual personal data is never used for external model training) [confirm]</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Legal and regulatory compliance</td>
                    <td className="py-3 px-4 align-top">All categories where required</td>
                    <td className="py-3 px-4 align-top">Compliance with a legal obligation</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Separator className="bg-border/60" />

          {/* Section 4 */}
          <div ref={sec4Ref} className={`space-y-6 transition-all duration-700 ease-out ${sec4Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground flex items-baseline gap-3">
              <span className="font-mono text-lg" style={{
              color: "var(--primary)"
            }}>
                04 //
              </span>
              4. Children, schools and AI
            </h2>

            {/* Short Version for Students Callout Box */}
            <div className="my-8 p-6 md:p-8 bg-card border border-border rounded-md relative overflow-hidden">
              <div className="absolute top-0 left-0 bottom-0 w-1" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span className="font-mono text-xs uppercase tracking-widest block mb-2 font-medium" style={{
              color: "var(--primary)"
            }}>
                Summary Note
              </span>
              <h3 className="text-lg md:text-xl font-semibold mb-3 text-foreground">
                The short version for students:
              </h3>
              <p className="text-foreground/90 leading-relaxed">
                We only see the work you type or submit to find where you lost marks in an exam question. We do not sell your data, we do not track you across other websites, and we do not use your answers to train public AI models. Your school controls your data.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-foreground">School-Governed Architecture</h3>
              <p>
                When Lumina is deployed within an educational institution, the school is the Data Controller. We process student responses strictly as instructed by the school. Students cannot create public consumer profiles, interact with unregulated external forums, or share content publicly through our diagnostic engine.
              </p>

              <h3 className="text-xl font-semibold text-foreground">No Advertising, No Commercial Profiling</h3>
              <p>
                We maintain an absolute prohibition on advertising and commercial tracking. We do not display third-party advertisements, do not build behavioral profiles for commercial exploitation, and do not broker or monetize user data under any circumstance.
              </p>

              <h3 className="text-xl font-semibold text-foreground">Diagnostic Inference &amp; AI Infrastructure</h3>
              <p>
                Lumina maps student answers to mark-scheme criteria (such as M1, A1, B1 criteria) and isolates process errors at specific method steps. Where artificial intelligence models or rule layers are utilized:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/80">
                <li>Inputs submitted for diagnostic inference are strictly confined to the academic response and marking criteria.</li>
                <li>We contractually enforce zero-retention and zero-training policies with our inference infrastructure providers [confirm with provider terms]. Student submissions are not ingested into open training corpora.</li>
                <li>Diagnostics serve solely to assist teachers and students in understanding procedural step errors, not to generate autonomous, unreviewable disciplinary decisions.</li>
              </ul>
            </div>
          </div>

          <Separator className="bg-border/60" />

          {/* Section 5 */}
          <div ref={sec5Ref} className={`space-y-6 transition-all duration-700 ease-out ${sec5Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground flex items-baseline gap-3">
              <span className="font-mono text-lg" style={{
              color: "var(--primary)"
            }}>
                05 //
              </span>
              5. Who we share data with
            </h2>

            <p>
              We do not sell, rent, or trade personal data. We share data only with trusted third-party sub-processors necessary to deliver our services, enforce security, or comply with statutory legal mandates.
            </p>

            {/* Sub-processor Table */}
            <div className="overflow-x-auto my-6 border border-border rounded-md">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border bg-card/60">
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Sub-processor
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Purpose
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Location
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Safeguards
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Vercel</td>
                    <td className="py-3 px-4 align-top">Hosts the Setura app</td>
                    <td className="py-3 px-4 align-top">UK (London)</td>
                    <td className="py-3 px-4 align-top">[confirm]</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Anthropic</td>
                    <td className="py-3 px-4 align-top">Runs the Claude AI model that classifies steps the rules cannot. Receives working only, no names or IDs.</td>
                    <td className="py-3 px-4 align-top">US</td>
                    <td className="py-3 px-4 align-top">[confirm transfer safeguard]</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Supabase [planned, confirm]</td>
                    <td className="py-3 px-4 align-top">Stores accounts and diagnostic records</td>
                    <td className="py-3 px-4 align-top">UK (London region) </td>
                    <td className="py-3 px-4 align-top">[confirm]</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">10Web</td>
                    <td className="py-3 px-4 align-top">Hosts resseti.com</td>
                    <td className="py-3 px-4 align-top">US</td>
                    <td className="py-3 px-4 align-top">[confirm]</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Microsoft (Exchange Online)</td>
                    <td className="py-3 px-4 align-top">Business email, including correspondence with schools, enquiries and invoices</td>
                    <td className="py-3 px-4 align-top">UK </td>
                    <td className="py-3 px-4 align-top">Data processing agreement, encryption in transit</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Tide, with Adyen N.V. for card payments</td>
                    <td className="py-3 px-4 align-top">Issues invoices and receives payments. Card payments made through Tide Payment Links are processed by Adyen.</td>
                    <td className="py-3 px-4 align-top">UK (Tide), Netherlands (Adyen)</td>
                    <td className="py-3 px-4 align-top">PCI-DSS compliant, DPA</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm text-muted-foreground">
              Where personal data is transferred outside the UK or European Economic Area, we implement required legal transfer mechanisms including UK International Data Transfer Agreements (IDTA) or European Commission Standard Contractual Clauses (SCCs).
            </p>
          </div>

          <Separator className="bg-border/60" />

          {/* Section 6 */}
          <div ref={sec6Ref} className={`space-y-6 transition-all duration-700 ease-out ${sec6Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground flex items-baseline gap-3">
              <span className="font-mono text-lg" style={{
              color: "var(--primary)"
            }}>
                06 //
              </span>
              6. Retention, security and your rights
            </h2>

            <h3 className="text-xl font-semibold text-foreground">Data Retention Schedule</h3>
            <p>
              We retain personal data only for as long as necessary to fulfill the purposes for which it was collected, or as required by statutory record-keeping obligations.
            </p>

            {/* Retention Table */}
            <div className="overflow-x-auto my-6 border border-border rounded-md">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border bg-card/60">
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Data Type
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Retention Period
                    </th>
                    <th className="py-3 px-4 font-mono font-medium tracking-wide uppercase text-xs" style={{
                    color: "var(--primary)"
                  }}>
                      Rationale / Trigger
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Student Assessment Data (under school contract)</td>
                    <td className="py-3 px-4 align-top">Deleted within 24 months of school contract termination or upon school request</td>
                    <td className="py-3 px-4 align-top">School control and contract lifecycle</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">School Enquiry &amp; Pilot Contact Records</td>
                    <td className="py-3 px-4 align-top">Retained for 5 years from last active correspondence</td>
                    <td className="py-3 px-4 align-top">Maintaining institutional relationship history and pilot audit</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Technical Server &amp; Security Logs</td>
                    <td className="py-3 px-4 align-top">Retained for&nbsp; 12 months</td>
                    <td className="py-3 px-4 align-top">Threat monitoring, diagnostic troubleshooting, and incident response</td>
                  </tr>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="py-3 px-4 font-medium align-top">Inactive User Accounts</td>
                    <td className="py-3 px-4 align-top">Removed or anonymised after 2 years of total inactivity</td>
                    <td className="py-3 px-4 align-top">Data minimisation principles</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold text-foreground">Security Measures</h3>
            <p>
              We implement technical and organizational security controls designed to protect information from unauthorized access, accidental loss, disclosure, or alteration:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/80">
              <li>
                <strong className="text-foreground">Encryption:</strong> All data in transit is encrypted using modern TLS (Transport Layer Security 1.3), and stored data is encrypted at rest using AES-256 standard encryption.
              </li>
              <li>
                <strong className="text-foreground">Access Controls:</strong> Role-based access control (RBAC), multi-factor authentication for administrative staff, and strict principle-of-least-privilege access.
              </li>
              <li>
                <strong className="text-foreground">Monitoring &amp; Audit:</strong> Continuous logging and security reviews of infrastructure components.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground">Your Statutory Rights</h3>
            <p>
              Under UK and EU data protection laws, individuals hold specific statutory rights regarding their personal data:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-foreground/80">
              <li><strong className="text-foreground">Right of access:</strong> You can request a copy of the personal data we hold about you.</li>
              <li><strong className="text-foreground">Right to rectification:</strong> You can ask us to correct inaccurate or incomplete data.</li>
              <li><strong className="text-foreground">Right to erasure:</strong> You can ask us to delete your personal data under certain conditions.</li>
              <li><strong className="text-foreground">Right to restrict processing:</strong> You can ask us to pause processing your personal data.</li>
              <li><strong className="text-foreground">Right to data portability:</strong> You can request transfer of your data to another organization.</li>
              <li><strong className="text-foreground">Right to object:</strong> You can object to processing based on legitimate interests.</li>
            </ul>

            <div className="p-5 bg-card/60 border border-border rounded-md space-y-3 mt-6">
              <p className="text-sm font-medium text-foreground">
                How to exercise your rights:
              </p>
              <p className="text-sm text-foreground/80">
                <strong>For students using Lumina through a school:</strong> Because the school is the Data Controller, requests should be submitted directly to your school&apos;s data protection officer. We will assist your school in fulfilling valid requests.
              </p>
              <p className="text-sm text-foreground/80"><strong>For direct enquiries and website users:</strong> Email us at <a href="mailto:hello@resseti.com" target="_blank" rel="noopener noreferrer" data-link-type="url" data-link-href="mailto:hello@resseti.com" data-link-target="_blank" className="underline hover:opacity-80 transition-opacity underline cursor-pointer"><a href="mailto:[privacy@resseti.com]" className="underline hover:opacity-80 transition-opacity" contenteditable="false" style={{
                  color: 'var(--primary)'
                }}>privacy@resseti.com</a></a>. You also maintain the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at <span className="font-mono text-xs">ico.org.uk</span> or your relevant European supervisory authority.</p>
            </div>
          </div>
        </div>

        {/* Footer Accent Line */}
        <div className="mt-20 pt-8 border-t border-border flex justify-between items-center text-xs font-mono text-muted-foreground uppercase tracking-wider">
          <span>[Resseti Ltd] &mdash; Information Governance</span>
          <span>Version 1.0</span>
        </div>
      </div>
    </section>;
}